import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Error as ErrorIcon,
  WarningAmber as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

export interface ErrorDetails {
  error: string;
  details?: string;
  status?: number;
  endpoint?: string;
  timestamp?: string;
  /**
   * PHA-1210: 'notice' = the server (or a client-side check) refused the action for a
   * business reason the user can act on, not a fault. Rendered as a plain warning with its
   * own title — no "Server Error", HTTP status, endpoint, technical details or "contact
   * support" boilerplate.
   */
  kind?: 'error' | 'notice';
  title?: string;
}

interface ErrorModalProps {
  open: boolean;
  onClose: () => void;
  errorData: ErrorDetails | null;
  title?: string;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ 
  open, 
  onClose, 
  errorData,
  title = 'Server Error'
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!errorData) return null;

  if (errorData.kind === 'notice') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderTop: '4px solid', borderColor: 'warning.main' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" component="span" sx={{ flex: 1 }}>
            {errorData.title || 'Action needed'}
          </Typography>
          <IconButton aria-label="close" onClick={onClose} size="small" sx={{ color: 'grey.500' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {errorData.error}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const handleCopyDetails = () => {
    const detailsText = JSON.stringify(errorData, null, 2);
    navigator.clipboard.writeText(detailsText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const parseStackTrace = (details: string) => {
    // Try to parse Python traceback
    if (details.includes('Traceback')) {
      const lines = details.split('\n');
      return lines.map((line, index) => (
        <Typography
          key={index}
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            color: line.includes('File') ? 'error.main' : 
                   line.includes('Error') || line.includes('Assert') ? 'error.dark' : 
                   'text.secondary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {line}
        </Typography>
      ));
    }
    
    // For other error formats, display as-is
    return (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          color: 'text.secondary',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {details}
      </Typography>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: '4px solid',
          borderColor: 'error.main',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <ErrorIcon color="error" />
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {errorData.error || 'An unexpected error occurred'}
          </Typography>
          {errorData.status && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
              HTTP Status: {errorData.status}
            </Typography>
          )}
          {errorData.endpoint && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
              Endpoint: {errorData.endpoint}
            </Typography>
          )}
        </Alert>

        {errorData.details && (
          <Box>
            <Button
              startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowDetails(!showDetails)}
              size="small"
              variant="outlined"
              fullWidth
              sx={{ mb: 1, justifyContent: 'flex-start' }}
            >
              {showDetails ? 'Hide' : 'Show'} Technical Details
            </Button>

            <Collapse in={showDetails}>
              <Box
                sx={{
                  backgroundColor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: '400px',
                  overflowY: 'auto',
                  position: 'relative',
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleCopyDetails}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'white',
                    '&:hover': { backgroundColor: 'grey.200' },
                  }}
                  title="Copy to clipboard"
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
                
                {copied && (
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 48,
                      backgroundColor: 'success.light',
                      color: 'success.contrastText',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    Copied!
                  </Typography>
                )}

                <Box sx={{ pr: 5 }}>
                  {parseStackTrace(errorData.details)}
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.dark">
            <strong>What to do next:</strong>
          </Typography>
          <Typography variant="body2" color="info.dark" sx={{ mt: 1 }}>
            • Try refreshing the page and attempting the operation again
            <br />
            • If the error persists, please contact technical support
            <br />
            • Use the "Show Technical Details" button to copy error information for support
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorModal;
